import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { useCallback, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../Button/Button';
import { ELEVATION } from '../../theme/elevation';
import { ChatAttachements } from './ChatAttachements';

export type ChatInputProps = {
  attachments?: {
    files: File[];
    onUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onRemove: (file: File) => void;
  };
  sendButton: {
    label?: string;
    onClick: (message: string) => void;
    isLoading?: boolean;
  };
  cancelMessage?: () => void;
  minRows?: number;
  maxRows?: number;
  placeholder?: string;
  inputRef?: React.RefObject<{ setValue: (value: string) => void } | null>;
};

export function ChatInput({
  attachments,
  cancelMessage,
  sendButton,
  minRows = 3,
  maxRows = 6,
  placeholder,
  inputRef,
}: ChatInputProps) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const inputRefInternal = useRef<HTMLInputElement>(null);

  useImperativeHandle(inputRef, () => ({
    setValue: (value: string) => {
      setQuery(value);
      if (inputRefInternal.current) {
        inputRefInternal.current.focus();
      }
    },
  }));

  const handleSendMessage = useCallback(() => {
    const trimmed = query.trim();
    if (trimmed) {
      sendButton.onClick(trimmed);
      setQuery('');
    }
  }, [sendButton, query]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter' && !event.shiftKey && !sendButton.isLoading) {
        event.preventDefault();
        handleSendMessage();
      }
    },
    [handleSendMessage, sendButton.isLoading]
  );

  return (
    <Box
      sx={theme => ({
        width: '100%',
        boxShadow: ELEVATION.elevation0,
        borderRadius: '2px',
        overflow: 'hidden',
        border: `1px solid ${theme.palette.divider}`,
      })}
    >
      <TextField
        autoFocus={true}
        multiline
        minRows={minRows}
        maxRows={maxRows}
        fullWidth
        placeholder={placeholder ?? t('What do you want to ask?')}
        value={query}
        onChange={e => setQuery(e.target.value)}
        onKeyDown={handleKeyDown}
        inputRef={inputRefInternal}
        slotProps={{
          input: {
            autoFocus: true,
            sx: {
              border: 'none',
              ':not(.Mui-disabled)&:hover': { backgroundColor: 'background.paper' },
              '&:focus': { outline: 'none' },
              pt: 1,
              pb: 0.5,
              px: 2,
              lineHeight: 2,
              fontSize: 15,
            },
          },
        }}
      />
      <Stack
        direction="row"
        sx={{
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 1,
          backgroundColor: 'background.paper',
          px: 1.5,
          pb: 1,
        }}
      >
        {attachments !== undefined && (
          <ChatAttachements
            attachments={attachments.files}
            onUpload={attachments.onUpload}
            onRemove={attachments.onRemove}
          />
        )}
        {sendButton.isLoading && cancelMessage !== undefined && (
          <Button variant="text" size="small" onClick={cancelMessage}>
            {t('Cancel')}
          </Button>
        )}
        <Button
          loading={sendButton.isLoading}
          variant="contained"
          color="primary"
          size="small"
          onClick={handleSendMessage}
          disabled={!query.trim()}
        >
          {sendButton.label ?? t('Ask')}
        </Button>
      </Stack>
    </Box>
  );
}
